import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setCanonical } from "@/lib/canonical";

/**
 * Handles:
 * 1. Trailing slash enforcement (non-trailing → trailing)
 * 2. Query parameter cleanup:
 *    - ?s=* → redirect to homepage (search crawl waste)
 *    - ?listing=*, ?actris=*, any other params → redirect to clean path
 * 3. Adds noindex meta tag as fallback for any parameterized URL
 */
// Params that must survive (checkout confirmation + attribution)
const PRESERVED_PARAMS = [
  "session_id",
  "success",
  "canceled",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "msclkid",
  "ref",
];

const TrailingSlashRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setCanonical(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const { pathname, search, hash } = location;

    // Don't redirect file requests (e.g., /sitemap.xml, /robots.txt)
    if (pathname.includes(".")) return;

    const cleanPath = pathname.endsWith("/") ? pathname : `${pathname}/`;

    // Handle query parameters — redirect before trailing slash logic
    if (search) {
      const params = new URLSearchParams(search);

      // ?s= search queries → redirect to homepage
      if (params.has("s")) {
        navigate("/", { replace: true });
        return;
      }

      // Keep preserved params, drop the rest (?listing=, ?actris=, etc.)
      const kept = new URLSearchParams();
      params.forEach((value, key) => {
        if (PRESERVED_PARAMS.includes(key)) kept.append(key, value);
      });

      const keptSearch = kept.toString();
      const nextUrl = `${cleanPath}${keptSearch ? `?${keptSearch}` : ""}${hash}`;
      const currentUrl = `${pathname}${search}${hash}`;
      if (nextUrl !== currentUrl) {
        navigate(nextUrl, { replace: true });
      }
      return;
    }

    // Enforce trailing slash
    if (!pathname.endsWith("/")) {
      navigate(`${cleanPath}${hash}`, { replace: true });
      return;
    }
  }, [location, navigate]);


  // Fallback: if a search engine somehow renders a parameterized URL,
  // inject noindex meta tag to prevent indexing
  useEffect(() => {
    const { search } = location;
    if (!search) return;

    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const hadExisting = !!meta;
    const previousContent = meta?.content;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.content = "noindex, follow";

    return () => {
      if (!hadExisting && meta) {
        meta.remove();
      } else if (meta) {
        meta.content = previousContent ?? "";
      }
    };
  }, [location]);

  return null;
};

export default TrailingSlashRedirect;
